import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
} from "react-table";
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiPlus, FiMinus } from 'react-icons/fi';

import "./Table.css";
import { IotmateButton, IotmateInput } from "../Shared";

export const Table = ({ columns, data, heading, onClick, typeComplex, expandable = false }) => {
  const [sortedColumns, setSortedColumns] = useState([]);
  const [filter, setFilter] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  let initialPageIndex = (typeComplex ? parseInt(localStorage.getItem('pageIndex'), 10) || 0 : 0);

  if (initialPageIndex > (data.length / 10))
    initialPageIndex = 0

  const navigate = useNavigate();

  // Toggle row expansion
  const toggleRowExpanded = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  // Check if a row is expanded
  const isRowExpanded = (rowId) => {
    return !!expandedRows[rowId];
  };

  const tableColumns = React.useMemo(() => {
    return columns;
  }, [columns]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    state,
    page,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    setGlobalFilter,
    gotoPage,
    pageCount,
    setPageSize,
    prepareRow,
  } = useTable(
    {
      columns: tableColumns,
      data,
      initialState: {
        pageIndex: initialPageIndex,
        sortBy: sortedColumns,
        globalFilter: filter,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { pageIndex, pageSize, globalFilter, sortBy } = state;

  React.useEffect(() => {
    if (typeComplex) {
      localStorage.setItem('pageIndex', pageIndex);
      setSortedColumns(sortBy);
      setFilter(globalFilter);
    }
  }, [pageIndex, data, sortBy, globalFilter, typeComplex]);

  const handleRowClick = (row) => {
    if (!onClick) return;

    // If onClick is a function, call it with row data
    if (typeof onClick === 'function') {
      onClick(row);
    } else {
      // Legacy behavior: onClick is a string path
      navigate(onClick + "/" + row.values["oms_id"]);
    }
  };

  // Render expander cell
  const renderExpanderCell = (row, hasChildren) => {
    if (!expandable) return null;

    const rowId = row.original.oms_id || row.id;
    const isExpanded = isRowExpanded(rowId);

    return (
      <td
        style={{ width: '24px', minWidth: '24px', maxWidth: '24px', textAlign: 'right', paddingRight: '4px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {hasChildren ? (
          <span
            className="expander-btn"
            onClick={(e) => {
              e.stopPropagation();
              toggleRowExpanded(rowId);
            }}
          >
            {isExpanded ? (
              <FiMinus size={14} />
            ) : (
              <FiPlus size={14} />
            )}
          </span>
        ) : null}
      </td>
    );
  };

  // Render a child row
  const renderChildRow = (childData, parentIndex, childIndex) => {
    return (
      <tr
        key={`child-${parentIndex}-${childIndex}`}
        className="child-row"
        onClick={() => onClick && onClick({ values: childData, original: childData })}
        style={{ cursor: onClick ? "pointer" : "default" }}
      >
        {expandable && (
          <td style={{ width: '24px', minWidth: '24px', maxWidth: '24px', textAlign: 'right', paddingRight: '4px' }}>
            <span style={{ color: '#9ca3af', fontSize: '12px' }}>└</span>
          </td>
        )}
        {columns.map((column, colIndex) => (
          <td
            key={colIndex}
            style={{
              paddingLeft: colIndex === 0 ? '24px' : undefined,
              borderLeft: colIndex === 0 ? '3px solid #667eea' : undefined,
            }}
          >
            {column.Cell
              ? column.Cell({ value: childData[column.accessor], row: { original: childData, values: childData } })
              : childData[column.accessor]
            }
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className="w-full">
      <div className="mb-4 flex justify-between items-center flex-wrap gap-4">
        <div className="Heading">{heading}</div>
        {typeComplex && (
          <div className="flex items-center gap-3">
            <IotmateInput
              type="text"
              placeholder="Search across all columns..."
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              width="300px"
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table {...getTableProps()} className="rounded-lg w-full">
          <thead>
            {headerGroups.map((headerGroup, groupIndex) => (
              <tr key={groupIndex} {...headerGroup.getHeaderGroupProps()}>
                {expandable && (
                  <th style={{ width: '24px', minWidth: '24px', maxWidth: '24px' }}></th>
                )}
                {headerGroup.headers.map((column, columnIndex) => (
                  <th
                    key={columnIndex}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="cursor-pointer select-none"
                    data-column={column.Header}
                    data-index={columnIndex}
                    style={{
                      ...column.getHeaderProps(column.getSortByToggleProps()).style,
                      minWidth: columnIndex === 0 ? '120px' : undefined,
                      width: columnIndex === 0 ? '120px' : undefined,
                    }}
                  >
                    {column.render("Header")}
                    <span style={{ marginLeft: '0.5rem', fontSize: '1rem', opacity: 0.8 }}>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? "↓"
                          : "↑"
                        : "⇅"}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              const hasChildren = row.original.childDevices && row.original.childDevices.length > 0;
              const rowId = row.original.oms_id || row.id;
              const isExpanded = isRowExpanded(rowId);

              return (
                <React.Fragment key={row.id}>
                  <tr
                    {...row.getRowProps()}
                    className={`
                      ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      ${hasChildren ? "parent-row" : ""}
                      ${isExpanded ? "expanded" : ""}
                    `}
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: onClick ? "pointer" : "default" }}
                  >
                    {renderExpanderCell(row, hasChildren)}
                    {row.cells.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        {...cell.getCellProps()}
                        data-column={cell.column.Header}
                        data-index={cellIndex}
                        data-value={cell.value}
                        style={{
                          ...cell.getCellProps().style,
                          minWidth: cellIndex === 0 ? '120px' : undefined,
                          width: cellIndex === 0 ? '120px' : undefined,
                        }}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                  {/* Render child rows if expanded */}
                  {expandable && isExpanded && hasChildren && (
                    row.original.childDevices.map((child, childIndex) =>
                      renderChildRow(child, index, childIndex)
                    )
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {typeComplex && (
        <div className="mt-4 flex justify-between items-center flex-wrap gap-4 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center gap-2">
            <IotmateButton
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
              variant="outline"
              size="small"
            >
              <FiChevronsLeft size={16} />
            </IotmateButton>
            <IotmateButton
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              variant="outline"
              size="small"
            >
              <FiChevronLeft size={16} />
            </IotmateButton>
            <IotmateButton
              onClick={() => nextPage()}
              disabled={!canNextPage}
              variant="outline"
              size="small"
            >
              <FiChevronRight size={16} />
            </IotmateButton>
            <IotmateButton
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
              variant="outline"
              size="small"
            >
              <FiChevronsRight size={16} />
            </IotmateButton>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm text-gray-700 font-medium">
              Page <strong className="text-purple-600">{pageIndex + 1}</strong> of{" "}
              <strong className="text-purple-600">{pageOptions.length}</strong>
            </span>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Go to page:</span>
              <IotmateInput
                width="80px"
                type="number"
                placeholder=""
                value={pageIndex + 1}
                onChange={(e) => {
                  const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                  gotoPage(pageNumber);
                }}
              />
            </div>

            <select
              className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 25, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
